// Copyright (c) StrangeBee 2024

import React, { ReactElement, ReactNode, useRef } from 'react';
import type { Identifier, XYCoord } from 'dnd-core';
import { useDrag, useDrop } from 'react-dnd';
import EmptyElementToDrop from '@Organisms/Dashboard/DashboardDetails/EmptyElementToDrop/EmptyElementToDrop';
import { useLocalMessage } from '@Helpers/locale';
import useDebounce from '@Hooks/useDebounce';

interface DragItemParams {
    index: number;
    id: string;
    type: string;
    dragId: string;
}

interface ReportDropableElementProps {
    children: ReactNode;
    rowIndex: number;
    moveRow: (dragIndex: number, hoverIndex: number, item: DragItemParams) => void;
    onDrop?: (item: DragItemParams) => void;
    id: string;
    disabled?: boolean;
}

export const ReportDropableElement = ({
    children,
    id,
    rowIndex,
    moveRow,
    onDrop,
    disabled,
}: ReportDropableElementProps): ReactElement => {
    const i = useLocalMessage();
    const ref = useRef<HTMLDivElement>(null);
    const { debounce } = useDebounce();

    const [{ handlerId }, drop] = useDrop<DragItemParams, any, { handlerId: Identifier | null }>({
        accept: 'report-list',
        collect: (monitor) => ({
            handlerId: monitor.getHandlerId(),
        }),
        drop: (item) => {
            onDrop?.(item);
        },
        hover: (item, monitor) => {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = rowIndex;

            if (dragIndex === hoverIndex) {
                return;
            }

            const zoneHeight = 20;
            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            const hoverTopZoneY = 0;
            const hoverBottomZoneY = zoneHeight;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

            if (
                (dragIndex < hoverIndex && hoverClientY < hoverTopZoneY) ||
                (dragIndex > hoverIndex && hoverClientY > hoverBottomZoneY)
            ) {
                return;
            }

            debounce({ onTimeout: () => moveRow(dragIndex, hoverIndex, item), timeoutDelay: 10 });

            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag({
        item: {
            type: 'report-list',
            id,
            index: rowIndex,
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        isDragging: (monitor) => (monitor.getItem()?.dragId || monitor.getItem()?.id) === id,
        canDrag: !disabled,
    });

    drag(drop(ref));

    return (
        <div ref={ref} data-handler-id={handlerId}>
            {isDragging ? <EmptyElementToDrop text={i('report-template.drop.drag')} /> : children}
        </div>
    );
};
